import "./RemittanceLookup.css";
import React, { useState, useContext, useRef, useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { useOktaAuth } from "@okta/okta-react";
import Accordion from "../../../components/Accordion";
import ErrorHandler from "../../../components/core/ErrorHandler";
import { LoadingSpinner } from "../../../components/layout/LoadingSpinner";
import RemittanceLookupForm from "./components/RemittanceLookupForm";
import { HistoryModal, EditConfirmationModal } from "./components/HistoryModal";
import RemittanceLookupDirectExport from "./components/RemittanceLookupDirectExport";

import { GridColumn } from "@progress/kendo-react-grid";
import { ZIndexContext } from "@progress/kendo-react-common";

import SharedMainSelectableClientGrid from "shared/components/grids/clientGrid/SharedMainSelectableClientGrid";
import useGridDataStateResetProcessing from "shared/hooks/useGridDataStateResetProcessing";
import useGridButtons from "shared/components/grids/hooks/useGridButtons";

import {
  MoneyCell,
  AggregateMoneyFooterCell,
} from "../../../components/grid/Kendo/CustomKendoGridCells";

import {
  DefaultColumnWidth,
  DefaultWideColumnWidth,
  DefaultDateColumnWidth,
  DefaultBooleanOrShortWidthColumnWidth,
} from "../../../components/grid/Kendo/KendoGridAndColumnConstants";

import { UserContext } from "shared/context/UserContext";

import { useRemittanceLookupLogic } from "./hooks/useRemittanceLookupLogic";
import { PaymentDateCell } from "./components/PaymentDateCell";
import { ArchivedDateCell } from "./components/ArchivedDateCell";
import { RemittanceLookupActionCell } from "./components/RemittanceLookupActionCell";
import { useReactQueryHelpers } from "shared/hooks/useReactQueryHelpers";

export default function RemittanceLookup(props) {
  const { oktaAuth } = useOktaAuth();
  const [submittedFormData, setSubmittedFormData] = useState(null);
  const [error, setError] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [mainGridAggregates, setMainGridAggregates] = useState([]);
  const [directExportIsProcessing, setDirectExportIsProcessing] =
    useState(false);
  const [directExportType, setDirectExportType] = useState(null);
  const [isDirectExportDataLoading, setIsDirectExportDataLoading] =
    useState(false);

  const userContext = useContext(UserContext);
  const clientGridRef = useRef();
  const { invalidateReactQueryKey } = useReactQueryHelpers();

  const {
    gridDataStateResetProcessingInitiated,
    InitiateGridDataStateReset,
    GridDataStateResetCompleted: onGridDataStateResetCompleted,
  } = useGridDataStateResetProcessing();

  const {
    showArchiveModal,
    setShowArchiveModal,
    showUpdateEditConfirmation,
    setShowUpdateEditConfirmation,
    checkHistoryGridData,
    checkHistoryGridColumns,
    checkHistoryGridDataState,
    checkHistoryGridResultState,
    onDataStateChange,
    handleArchiveSelected,
    handleRestorePendingSelected,
    handlePayerExcelExport,
    handlePayerCSVExport,
    handleViewHistory,
    handleEditManualRemittance,
    handleEditConfirmation,
    handleUpdatePaymentDate,
    handleArchiveSingle,
    handleRestorePendingSingle,
    isThereAnExistingIncompleteManualRemittance,
  } = useRemittanceLookupLogic(
    oktaAuth,
    submittedFormData,
    setError,
    clientGridRef,
    setIsLoading
  );

  const { AddGridButton, gridButtons } = useGridButtons();

  const sendAggregatesToParent = (aggregatesFromSharedGrid) => {
    setMainGridAggregates(aggregatesFromSharedGrid.data);
  };

  // Set up grid buttons
  React.useEffect(() => {
    AddGridButton(
      "Archive Selected",
      () => {
        const selectedData = clientGridRef.current
          ? clientGridRef.current.GetSelectedGridDataKeys()
          : [];
        handleArchiveSelected(selectedData);
      },
      true
    );

    AddGridButton(
      "Restore Pending Selected",
      () => {
        const selectedData = clientGridRef.current
          ? clientGridRef.current.GetSelectedGridDataKeys()
          : [];
        handleRestorePendingSelected(selectedData);
      },
      true
    );
  }, []);

  // Clear grid and form data when organization changes
  React.useEffect(() => {
    // Reset the form submission state so grid has no parameters
    setSubmittedFormData(null);

    // Clear aggregates
    setMainGridAggregates([]);

    // Invalidate all cached grid data
    invalidateReactQueryKey(["useFetchClientGridDataDataKey"]);

    // Reset the grid's internal state (pagination, sorting, filters, selections)
    InitiateGridDataStateReset();
  }, [userContext.currentOrganization]);

  const gridApiParams = useMemo(() => {
    if (!submittedFormData) return null;

    return {
      user: userContext.email,
      Payers: submittedFormData.payers,
      Locations: submittedFormData.locations,
      Status: submittedFormData.status,
      FromDate: submittedFormData.fromDate,
      ToDate: submittedFormData.toDate,
      DateType: submittedFormData.dateType,
      CheckAmount: submittedFormData.amount,
      CheckNumber: submittedFormData.checkNumber,
    };
  }, [submittedFormData, userContext.email, userContext.currentOrganization]);

  function handleFormSubmit(formInput) {
    InitiateGridDataStateReset();
    setSubmittedFormData(formInput);
  }

  const MainGridAggregateMoneyFooterCell = (props) => {
    const options = {
      aggregateData: mainGridAggregates,
    };
    return AggregateMoneyFooterCell(props, options);
  };

  const handleExportPayerList = (exportType) => {
    setDirectExportIsProcessing(true);
    setDirectExportType(exportType);
  };

  const getExportData = async () => {
    if (directExportType === "excel") {
      return await handlePayerExcelExport();
    } else if (directExportType === "csv") {
      return await handlePayerCSVExport();
    }
    return null;
  };

  const isExportProcessing =
    directExportIsProcessing && isDirectExportDataLoading;
  const featureClass = "feature" + (isExportProcessing ? " disabled" : "");

  return (
    <div className={featureClass}>
      <canvas
        id="hiddenCanvasForSingleCheckPdf"
        style={{ display: "none" }}
        width={185}
        height={70}
      />

      <LoadingSpinner
        isDataLoading={isLoading}
        controlsName={"RemittanceLookup"}
      />

      <ErrorHandler
        error={error}
        onClose={() => {
          setError({});
        }}
      />

      <Accordion defaultExpanded label="Search & Filter">
        <Row>
          <Col md="auto" className="d-flex flex-column higher-zindex-filters">
            <RemittanceLookupForm
              handleFormSubmit={handleFormSubmit}
              handlePayerExport={handleExportPayerList}
            />
          </Col>
        </Row>
      </Accordion>

      {isExportProcessing && (
        <Row>
          <Col md={10} style={{ textAlign: "right" }}>
            <div
              style={{
                fontSize: "small",
                color: "red",
                fontWeight: "bold",
                fontStyle: "italic",
                padding: "10px",
              }}
            >
              Export is processing
            </div>
          </Col>
        </Row>
      )}

      <Row>
        <Col className="tight-grid multi-line-filter">
          <SharedMainSelectableClientGrid
            ref={clientGridRef}
            isSelectable={true}
            apiGetEndpoint={"api/RemittanceLookup/Get"}
            apiParameters={gridApiParams}
            dataItemKey={"pmid"}
            aggregateColumnSettings={[
              { aggregate: "sum", field: "adjusted" },
              { aggregate: "sum", field: "paid" },
              { aggregate: "sum", field: "checkAmount" },
            ]}
            sendAggregatesParentCallback={sendAggregatesToParent}
            exportFileNamePrefix={"RemittanceManagement"}
            gridButtons={gridButtons}
            gridDataStateResetProcessingInitiated={
              gridDataStateResetProcessingInitiated
            }
            onGridDataStateResetCompleted={onGridDataStateResetCompleted}
            dateFormattingColumns={[
              "remittanceDate",
              "archivedDate",
              "checkDate",
              "paymentDate",
            ]}
            pageSize={200}
          >
            <GridColumn
              field=""
              title="Action"
              filterable={false}
              sortable={false}
              groupable={false}
              width={DefaultBooleanOrShortWidthColumnWidth()}
              cells={{
                data: (props) => (
                  <RemittanceLookupActionCell
                    {...props}
                    onViewHistory={handleViewHistory}
                    onEditManualRemittance={handleEditManualRemittance}
                    onArchive={handleArchiveSingle}
                    onRestorePending={handleRestorePendingSingle}
                    oktaAuth={oktaAuth}
                  />
                ),
              }}
            />
            <GridColumn
              field="payer"
              title="Payer"
              width={DefaultColumnWidth()}
            />
            <GridColumn
              field="checkNumber"
              title="Check Number"
              width={DefaultColumnWidth()}
            />
            <GridColumn
              field="status"
              title="Remit Status"
              width={DefaultBooleanOrShortWidthColumnWidth()}
            />
            <GridColumn
              field="checkDate"
              title="Check Date"
              filter="date"
              format="{0:d}"
              width={DefaultDateColumnWidth()}
            />
            <GridColumn
              field="remittanceDate"
              title="Remit Date"
              filter="date"
              format="{0:d}"
              width={DefaultDateColumnWidth()}
            />
            <GridColumn
              field="paymentDate"
              title="Payment Date"
              filter="date"
              format="{0:d}"
              cells={{
                data: (props) => (
                  <PaymentDateCell
                    {...props}
                    onUpdatePaymentDate={handleUpdatePaymentDate}
                  />
                ),
              }}
              width={DefaultDateColumnWidth()}
            />
            <GridColumn
              field="ncpdp"
              title="NCPDP"
              width={DefaultBooleanOrShortWidthColumnWidth()}
            />
            <GridColumn
              field="checkAmount"
              title="Check Amount"
              filter="numeric"
              width={DefaultColumnWidth()}
              cells={{
                data: MoneyCell,
                footerCell: MainGridAggregateMoneyFooterCell,
              }}
            />
            <GridColumn
              field="adjusted"
              title="Adjusted"
              format="{0:c}"
              filter="numeric"
              cells={{
                data: MoneyCell,
                footerCell: MainGridAggregateMoneyFooterCell,
              }}
              width={DefaultColumnWidth()}
            />
            <GridColumn
              field="paid"
              title="Claims Amount"
              format="{0:c}"
              filter="numeric"
              cells={{
                data: MoneyCell,
                footerCell: MainGridAggregateMoneyFooterCell,
              }}
              width={DefaultColumnWidth()}
            />
            <GridColumn
              field="archivedDate"
              title="Archive Date"
              width={DefaultDateColumnWidth()}
              filter="date"
              format="{0:d}"
              cells={{
                data: ArchivedDateCell,
              }}
            />
          </SharedMainSelectableClientGrid>
        </Col>
      </Row>

      <RemittanceLookupDirectExport
        email={userContext.email}
        directExportIsProcessing={directExportIsProcessing}
        setDirectExportIsProcessing={setDirectExportIsProcessing}
        directExportType={directExportType}
        setIsDirectExportDataLoading={setIsDirectExportDataLoading}
        onExportSuccess={getExportData}
      />

      <ZIndexContext.Provider value={10003}>
        <HistoryModal
          title="Check History"
          show={showArchiveModal}
          handleClose={() => setShowArchiveModal(false)}
          gridData={checkHistoryGridData}
          gridColumns={checkHistoryGridColumns}
          gridDataState={checkHistoryGridDataState}
          gridResultState={checkHistoryGridResultState}
          onDataStateChange={onDataStateChange}
        />
      </ZIndexContext.Provider>

      <ZIndexContext.Provider value={10005}>
        <EditConfirmationModal
          title="Edit Manual Remittance - Confirmation"
          show={showUpdateEditConfirmation}
          handleClose={() => setShowUpdateEditConfirmation(false)}
          handleOk={handleEditConfirmation}
        >
          <Row>
            <Col>
              {isThereAnExistingIncompleteManualRemittance
                ? "WARNING: You have an INCOMPLETE manual remittance in-progress which will be ERASED if you begin editing this manual remittance. Do you want to continue?"
                : "Editing the manual remittance will DELETE the current version and un-match all claims included. Do you want to continue?"}
            </Col>
          </Row>
        </EditConfirmationModal>
      </ZIndexContext.Provider>
    </div>
  );
}
